<template>
  <!-- Details Sheet -->
  <f7-sheet
    v-model:opened="sheetsState.details"
    swipe-to-close
    push
    backdrop
  >
    <f7-toolbar>
      <div class="left" />
      <div class="center">{{ $t('item.detail.title') }}</div>
      <div class="right">
        <f7-link sheet-close>{{ $t('general.close') }}</f7-link>
      </div>
    </f7-toolbar>
    <f7-page-content>
      <template v-if="item">
        <f7-card>
          <f7-card-content :padding="false">
            <div class="item-overview">
              <div class="overview-header">
                <!-- Manual (off-catalog) lines have no catalog image — render
                     the branded monogram default instead of a broken <img>. -->
                <manual-item-avatar
                  v-if="isManual"
                  :label="displayName"
                  :size="80"
                  :alt="$t('item.manual.avatarAlt')"
                ></manual-item-avatar>
                <img
                  v-else
                  :src="item.avatarUrl || item.productAvatarUrl"
                  class="overview-image"
                  :alt="displayName"
                />
                <div class="overview-info">
                  <div class="overview-title">{{ displayName }}</div>
                  <div v-if="!isManual && item.channelName" class="overview-subtitle">{{ item.channelName }}</div>
                  <f7-chip :text="localizedStatus" :color="statusColor"></f7-chip>
                </div>
              </div>

              <!-- Manual-line hint — reassure the member the team added this
                   item to the box directly (not from the feed). -->
              <div v-if="isManual" class="manual-hint">
                <f7-icon f7="sparkles" size="16" class="manual-hint__icon"></f7-icon>
                <span>{{ $t('item.manual.hint') }}</span>
              </div>
            </div>

            <div class="card-divider" />

            <div class="item-details-list">
              <f7-list no-hairlines no-hairlines-between style="margin: 0;">
                <f7-list-item v-if="!isManual && item.variantName">
                  <template #title>
                    <span class="detail-label">{{ $t('item.detail.variantLabel') }}</span>
                  </template>
                  <template #after>{{ item.variantName }}</template>
                </f7-list-item>
                <f7-list-item>
                  <template #title>
                    <span class="detail-label">{{ $t('item.detail.quantity') }}</span>
                  </template>
                  <template #after>{{ item.qty }}</template>
                </f7-list-item>
                <f7-list-item>
                  <template #title>
                    <span class="detail-label">{{ $t('item.detail.unitPrice') }}</span>
                  </template>
                  <template #after>{{ unitPrice }}</template>
                </f7-list-item>
                <f7-list-item v-if="totalPrice">
                  <template #title>
                    <span class="detail-label detail-total">{{ $t('item.detail.totalPrice') }}</span>
                  </template>
                  <template #after>
                    <span class="detail-total">{{ totalPrice }}</span>
                  </template>
                </f7-list-item>
              </f7-list>
            </div>

            <div v-if="item.notes" class="item-notes">
              <div class="notes-title">{{ $t('item.detail.notes') }}</div>
              <div class="notes-content">{{ item.notes }}</div>
            </div>
          </f7-card-content>
        </f7-card>

        <f7-block-title>{{ $t('item.timeline.title') }}</f7-block-title>
        <div v-if="timeline.length > 0" class="timeline">
          <div
            v-for="(timelineItem, index) in timeline"
            :key="index"
            class="timeline-item"
          >
            <div class="timeline-item-date">
              {{ timelineItem.time }}
              <small>{{ timelineItem.date }}</small>
            </div>
            <div class="timeline-item-divider" />
            <div class="timeline-item-content">
              <div class="timeline-item-inner">
                <div class="timeline-item-title">{{ timelineItem.title }}</div>
                <div class="timeline-item-subtitle">{{ timelineItem.subtitle }}</div>
                <div v-if="timelineItem.description" class="timeline-item-text">
                  {{ timelineItem.description }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <f7-block v-else class="text-align-center text-color-gray">
          <p>{{ $t('item.timeline.noData') }}</p>
        </f7-block>

        <!-- Calm, issue-aware state — replaces the retired member resolution CTA
             (change-supplier / wait-restock / cancel). The member is
             reactive-only: an item mid-issue (leader's court) or out of stock is
             shown as an honest status, never a self-service action (the backend
             blocks member mutations on in-plan issue items anyway — a 403
             dead-end).

             What still reaches this branch is NARROW. Any item with a live typed
             issue behind it — the member's turn OR the leader's — now row-routes
             to its issue card instead of opening this sheet (isRoutableIssue in
             useItemIssuePresentation). So BEING_HANDLED lands here only when
             there is no issue to open: the member-issues flag is off, or the
             issue was raised leader-direct with no item_issues row. OUT_OF_STOCK
             now row-routes too whenever it carries issue history (`lastIssueId`,
             ASK 8), so it lands here only when there is likewise nothing to
             open. -->
        <f7-block
          v-if="issueState === ISSUE_PRESENTATION.BEING_HANDLED
            || issueState === ISSUE_PRESENTATION.OUT_OF_STOCK"
          class="issue-state"
          :class="`issue-state--${issueState}`"
        >
          <f7-icon
            :f7="issueState === ISSUE_PRESENTATION.OUT_OF_STOCK ? 'xmark_circle' : 'arrow_2_circlepath'"
            size="22"
            class="issue-state__icon"
          ></f7-icon>
          <div class="issue-state__text">
            <div class="issue-state__title">{{ issueStateTitle }}</div>
            <div class="issue-state__body">{{ issueStateBody }}</div>
            <!-- THE MONEY SENTENCE (U-70). Only where it is true: an
                 out-of-stock line inside a carton that can still take the item
                 back is STILL HOLDING her money — it counts toward her
                 committed balance and her available balance is lower by it —
                 and the only thing that releases it is the cancel below or the
                 carton leaving that window.
                 Paired with the cancel control on purpose: a sentence about
                 money she can act on, next to the act. -->
            <div v-if="canCancelOutOfStock" class="issue-state__money">
              {{ $t('collection.state.outOfStockMoneyNote') }}
            </div>
          </div>
        </f7-block>

        <!-- HER DOOR. "Nobody could get it — but I know a shop that has it."
             Offered on a line that is out of stock, or still waiting and not
             yet on a run; `canSuggestSupplier` mirrors the backend policy and
             explains the one arm it can only approximate.

             Suppressed while a suggestion is still OPEN: she has already told
             us, and a send button sitting beside «وصل اقتراحك للفريق» reads as
             though the first one never registered. Correcting it is the quiet
             text link under that line instead.

             Outline, not fill: this is an offer, not something being asked of
             her — same weight as the issue-history button below it. -->
        <f7-block v-if="canSuggest" class="suggest-supplier">
          <f7-button
            outline
            large
            class="suggest-supplier__btn"
            @click="openSuggestSheet"
          >
            <f7-icon f7="map_pin_ellipse" size="18" class="margin-end-half"></f7-icon>
            {{ $t('item.suggestSupplier.cta') }}
          </f7-button>
        </f7-block>

        <!-- Her tip, and what the club said back. The same quiet line the row
             carries — one writer, so it cannot drift.

             NO LONGER `v-else-if`. The receipt and the door used to be mutually
             exclusive because the receipt only ever meant "still waiting"; now
             that it also carries the ANSWER, an answered tip renders here WITH
             the door open above it. That pairing is the point: she reads what
             happened, and the next shop is one tap away.

             «غيّر اقتراحك» stays gated on an OPEN tip. Changing a suggestion the
             club has already answered is not a correction, it is a new tip —
             which is exactly what the re-opened door above is for. -->
        <f7-block v-if="suggestionReceipt" class="suggest-supplier suggest-supplier--pending">
          <p class="suggest-supplier__pending">{{ suggestionReceipt }}</p>
          <f7-link v-if="canCorrectSuggestion" @click="openSuggestSheet">
            {{ $t('item.suggestSupplier.pending.change') }}
          </f7-link>
        </f7-block>

        <!-- HER RETURN DOOR — «أرجّع الصنف» (returns-desk P3-A part A). A
             DELIVERED line whose carton has taken delivery confirmation, is
             not a Poola line, and carries no OPEN return of its own. Outline,
             same weight as the suggest-a-shop door above: an offer, not
             something being asked of her. Can render TOGETHER with the status
             block below it — a refunded/coming-back return is closed, and the
             door is exactly what re-opens then, same as an answered shop tip
             sits under its own reopened door. -->
        <f7-block v-if="canReturn" class="request-return">
          <f7-button
            outline
            large
            class="request-return__btn"
            @click="openRequestReturnSheet"
          >
            <f7-icon f7="arrow_uturn_left" size="18" class="margin-end-half"></f7-icon>
            {{ $t('item.return.cta') }}
          </f7-button>
        </f7-block>

        <!-- «قطع التدبيسة مالهاش إرجاع» — a quiet note where the return door
             above would have been, on a تدبيسة line. Not an error state and
             not actionable, so it gets no button weight, just the fact. -->
        <f7-block v-if="showTadbeesaFinalSale" class="tadbeesa-final-sale">
          <f7-icon f7="nosign" size="16" class="tadbeesa-final-sale__icon"></f7-icon>
          <span>{{ $t('product.resale.finalSale') }}</span>
        </f7-block>

        <!-- HER RETURN'S STATUS — one of the six words, presence-driven (never
             tested against a specific value). The reason rides beside it as
             her own quiet chip, exactly as PART B of this pack asks. -->
        <f7-block v-if="supplierReturn" class="return-status">
          <p class="return-status__text">{{ returnStatusText }}</p>
          <f7-chip
            v-if="returnReasonText"
            :text="returnReasonText"
            outline
            class="return-status__reason-chip"
          ></f7-chip>
        </f7-block>

        <!-- "What happened with this one" — the details-sheet half of ASK 8.
             An item whose issue is over is back to an ordinary status and keeps
             its ordinary row (tapping it opens THIS sheet, not the issue card),
             so without this its history would still dead-end. `lastIssueId`
             survives the resolution, and `show` is state-agnostic for the owner,
             so the same /issues/:id/ card opens read-only with the leader's
             message and the full timeline. Quiet and outline: this is a look-up,
             not something being asked of the member. -->
        <f7-block v-if="readableIssueId" class="issue-history">
          <f7-button
            outline
            large
            class="issue-history__btn"
            @click="openIssueHistory"
          >
            <f7-icon f7="clock" size="18" class="margin-end-half"></f7-icon>
            {{ $t('collection.history.openIssue') }}
          </f7-button>
        </f7-block>

        <!-- CANCEL, ON THE DETAILS SHEET ITSELF (oos-hold P1-A, owner answer
             H-M). Not inside the edit sheet, and the difference matters: an
             out-of-stock line is not EDITABLE — no quantity, no note — so the
             edit sheet never opens for it and the control that lives in there
             was unreachable on exactly the lines that now need it.

             Offered only inside the carton window, because outside it the
             backend answers 422. Outline and last: this is hers to do, not
             something being asked of her. -->
        <f7-block v-if="canCancelOutOfStock" class="cancel-oos">
          <f7-button
            outline
            large
            color="red"
            class="cancel-oos__btn"
            :disabled="cancelling"
            @click="confirmCancelOutOfStock"
          >
            <f7-preloader v-if="cancelling" size="20" class="margin-end-half"></f7-preloader>
            <f7-icon v-else f7="xmark_circle" size="18" class="margin-end-half"></f7-icon>
            {{ $t('item.status.actions.cancelOutOfStock') }}
          </f7-button>
        </f7-block>

        <!-- HER OTHER DOOR — «اطلب الإلغاء» (BE→FE brief
             2026-09-21-member-cancel-request). Mutually exclusive with
             cancel-oos above by status: OUT_OF_STOCK gets that cancel; a line
             a collector is already out buying (`inActivePlan`, still
             PENDING/COLLECTING/ISSUE) gets this one instead — she cannot
             cancel it herself, only ask the admin to. Same weight, same
             one-layer confirm shape as the door above it. While her request
             waits the button becomes the small waiting chip below it, in the
             same spot — no toast, the chip replacing the button IS the
             confirmation (ux-simplifier decision). -->
        <f7-block v-if="canRequestCancelHere" class="request-cancel">
          <f7-button
            outline
            large
            color="red"
            class="request-cancel__btn"
            :disabled="requestingCancel"
            @click="confirmRequestCancel"
          >
            <f7-preloader v-if="requestingCancel" size="20" class="margin-end-half"></f7-preloader>
            <f7-icon v-else f7="xmark_circle" size="18" class="margin-end-half"></f7-icon>
            {{ $t('item.status.actions.requestCancel') }}
          </f7-button>
        </f7-block>
        <f7-block v-else-if="hasPendingCancelRequestHere" class="request-cancel request-cancel--pending">
          <f7-chip
            :text="$t('item.status.badges.cancelRequested')"
            outline
            color="orange"
            class="item-chip item-chip--cancel-requested"
          >
            <template #media>
              <f7-icon f7="clock" size="14"></f7-icon>
            </template>
          </f7-chip>
        </f7-block>

        <f7-block v-if="canEdit">
          <f7-button
            fill
            large
            :disabled="props.isUpdating"
            @click="openEditSheet"
          >
            <f7-preloader v-if="props.isUpdating" size="20" class="margin-end-half"></f7-preloader>
            <f7-icon v-else f7="pencil" class="margin-end-half"></f7-icon>
            {{ $t('item.actions.edit') }}
          </f7-button>
        </f7-block>
      </template>
    </f7-page-content>
  </f7-sheet>

  <!-- Edit Sheet with VariantItemsEditor — hosted directly in the sheet
       (CartPage pattern); a sheet must not contain a full f7-page (B7) -->
  <f7-sheet
    v-model:opened="sheetsState.edit"
    style="height: 90%;"
    :swipe-to-close="false"
    backdrop
    :close-by-backdrop-click="false"
    @sheet:closed="handleEditSheetClose"
  >
    <!-- Post data loads lazily on open (B6) — preloader until it resolves -->
    <div v-if="!variantData" class="edit-sheet-loading">
      <f7-preloader></f7-preloader>
    </div>
    <variant-items-editor
      v-if="item && variantData"
      v-model="currentVariantItems"
      :variant="variantData"
      :box-id="item.orderBoxId"
      :product-id="item.productId"
      :box-ship-name="boxShipName"
      :available-media="media"
      show-header
      sheet-mode
      @save-success="handleVariantSaveSuccess"
      @save-error="handleVariantSaveError"
      @cancel="handleCancelVariant"
      @close="handleEditSheetClose"
    ></variant-items-editor>
  </f7-sheet>

  <!-- The suggestion sheet. ONE instance beside the other two, for the same
       reason they are hoisted here (audit B7): a per-row instance would be 1×N
       sheets inside an f7-list. -->
  <supplier-suggestion-sheet
    v-model:opened="sheetsState.suggest"
    :item="item"
    :suppliers="suppliers"
    :reward-cents="rewardCents"
    :loading="suppliersLoading"
    :failed="suppliersFailed"
    @submitted="onSuggestionSubmitted"
    @stale="onSuggestionStale"
  ></supplier-suggestion-sheet>

  <!-- The return sheet. Same "one page-level instance" reasoning as the
       suggestion sheet beside it. -->
  <request-return-sheet
    v-model:opened="sheetsState.requestReturn"
    :item="item"
    @submitted="onReturnSubmitted"
    @stale="onReturnStale"
  ></request-return-sheet>

  <!-- RETIRED (member issue-flow unification): the legacy "Issue Resolution
       Sheet" (change-supplier / wait-restock / cancel) lived here. Every one of
       its actions 403'd for a member on an in-active-plan ISSUE item, so it was
       dead UX. The member is now reactive-only: the calm issue-state block in the
       details sheet shows an honest status, and a leader-sent decision is
       answered on the /issues/:id/ card. -->
</template>

<script setup>
/**
 * ItemSheets — single page-level instance of the details / edit / issue
 * sheets for OrdersPage rows (audit B7). Previously each ItemCard row carried
 * its own three f7-sheets as siblings of its f7-list-item, violating the
 * F7 v9 list DOM contract and creating 3×N sheet instances — which also made
 * the row component multi-root and blocked list virtualization (P9).
 *
 * The component is driven by a live `item` prop (the parent resolves it from
 * the ORM by id, so a fetchBox refresh keeps the open sheet's data current).
 */
import { useEventBus } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { useRepo } from 'pinia-orm';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ManualItemAvatar from '@/components/orders/ManualItemAvatar.vue';
import RequestReturnSheet from '@/components/orders/RequestReturnSheet.vue';
import SupplierSuggestionSheet from '@/components/orders/SupplierSuggestionSheet.vue';
import VariantItemsEditor from '@/components/orders/VariantItemsEditor.vue';
import { useItemStatus } from '@/composables/commerce/useItemStatus.js';
import { useSupplierList } from '@/composables/commerce/useSupplierList.js';
import { useVariantItemsLoader } from '@/composables/commerce/useVariantItemsLoader.js';
import { useCart } from '@/composables/useCart.js';
import { useCurrency } from '@/composables/useCurrency.js';
import { useIssueNavigation } from '@/composables/useIssueNavigation.js';
import {
  hasReadableIssue,
  ISSUE_PRESENTATION,
  useItemIssuePresentation,
} from '@/composables/useItemIssuePresentation.js';
import { useTadbeesaChannel } from '@/composables/useTadbeesaChannel.js';
import { useToast } from '@/composables/useToast.js';
import { WALLET_REFRESH_EVENT } from '@/constants/wallet.js';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import { ItemStatusMeta } from '@/enums/commerce/ItemStatusConfig.js';
import { OrderBoxStatus } from '@/enums/commerce/OrderBoxStatus.enum.js';
import OrderBox from '@/models/commerce/OrderBox.js';
import OrderBoxItem, { canRequestCancel, hasPendingCancelRequest } from '@/models/commerce/OrderBoxItem.js';
import Variant from '@/models/commerce/Variant.js';
import Post from '@/models/posts/Post.js';
import {
  cancelOrderBoxItem,
  ORDER_BOX_ITEM_STALE_STATE,
  requestCancelOrderBoxItem,
} from '@/services/orderBoxItem.service.js';
import { knownLineNetCents } from '@/utils/issueMoneyImpact.js';
import { isManualItem, itemDisplayName } from '@/utils/orderBoxItemDisplay.js';
import { formatDateTime } from '@/utils/orderUtils.js';
import {
  canRequestReturn,
  readSupplierReturn,
  returnReasonLabel,
  returnStatusLabel,
} from '@/utils/supplierReturn.js';
import {
  canSuggestSupplier,
  hasOpenSupplierSuggestion,
  readSupplierSuggestion,
  suggestionLabel,
} from '@/utils/supplierSuggestion.js';
import { isTadbeesaLine } from '@/utils/tadbeesa.js';

const props = defineProps({
  item: {
    type: Object,
    default: null,
  },
  boxStatus: {
    type: [String, Number],
    default: OrderBoxStatus.PENDING,
  },
  // The carton's own delivery-confirmation fact — the second half of
  // `boxCanTakeItBack()` beside `boxStatus` above. Threaded as its own
  // primitive prop rather than the whole box, matching how `boxStatus`
  // already carries just the one fact this sheet needs (oos-desk P3-E).
  boxDeliveredConfirmedAt: {
    type: [String, Date],
    default: null,
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const { t, locale } = useI18n();
const { formatCurrency } = useCurrency();
const walletRefreshBus = useEventBus(WALLET_REFRESH_EVENT);
// The carton is passed in so `canCancel` can answer for an OUT_OF_STOCK line:
// her money is held for one only while the carton can still take it back, and
// that is the same window the suggest door reads.
const itemStatus = useItemStatus(() => props.item, () => box.value);
const { loadVariantItems } = useVariantItemsLoader();
const { fetchPostData } = useCart();
const { showError, showSuccess } = useToast();
// The club's roster, for the suggestion sheet's picker. Fetched when that sheet
// opens, never on mount: this component is one page-level instance serving every
// row, so a mount-time fetch would ask on every visit to the orders tab whether
// or not she ever names a shop.
const {
  suppliers,
  rewardCents,
  loading: suppliersLoading,
  failed: suppliersFailed,
  load: loadSuppliers,
} = useSupplierList();

// Sheets state
const sheetsState = reactive({
  details: false,
  edit: false,
  suggest: false,
  requestReturn: false,
});

// Edit sheet state
const currentVariantItems = ref([]);
const variantData = ref(null);
const boxShipName = ref('');
const loading = ref(false);
const error = ref(null);
const media = ref(null);
const post = ref(null);
// B6: post data is fetched once per post, on first edit-sheet open — never on
// list mount. A single sheet instance serves every row, so cache by postId.
const loadedPostIds = new Set();

// Status display — all derived from useItemStatus, no manual mapping
const localizedStatus = itemStatus.label;
const statusColor     = itemStatus.color;

// Unified member issue presentation — drives the calm, non-actionable issue-state
// block in the details sheet (being-handled / out-of-stock). Replaces the retired
// isIssue-gated resolution CTA; the member is reactive-only.
const { presentation: issuePresentation } = useItemIssuePresentation(() => props.item);
const issueState = computed(() => issuePresentation.value.kind);
const issueStateTitle = computed(() =>
  issueState.value === ISSUE_PRESENTATION.OUT_OF_STOCK
    ? t('collection.state.outOfStock')
    : t('collection.state.beingHandled'),
);
const issueStateBody = computed(() =>
  issueState.value === ISSUE_PRESENTATION.OUT_OF_STOCK
    ? t('collection.state.outOfStockBody')
    : t('collection.state.beingHandledBody'),
);

// The item's readable issue, in ANY state — the broad predicate, not the row's
// `isRoutableIssue`. This sheet is reached precisely by the items whose row does
// NOT route (ordinary lines whose issue is over, and the id-less pill states),
// so it asks the wider question: is there anything to read at all?
const { openIssue } = useIssueNavigation();
const readableIssueId = computed(() =>
  hasReadableIssue(issuePresentation.value) ? issuePresentation.value.issueId : null,
);

// ── Her supplier suggestion ─────────────────────────────────────────────────
//
// Two mutually exclusive states, and the exclusivity is the point: a line either
// offers the door or reports that she has already walked through it. Both branch
// on the OMITTED-KEY reader, never on the raw attribute — see
// utils/supplierSuggestion.js.
const suggestion = computed(() => readSupplierSuggestion(props.item));

// Only an OPEN tip shuts the door. Once the club has answered, the door opens
// again UNDERNEATH the answer: she has read what happened and may well know a
// second shop, and an answered tip is history rather than a reason to stop her.
const stillWaiting = computed(() => hasOpenSupplierSuggestion(props.item));
// The fourth arm (oos-desk P3-E): a carton that has moved on cannot take the
// item back even while the line itself still qualifies. `boxStatus` is left
// OMITTED from the call, not passed as a garbage box, while it is unknown —
// `undefined` is the box-arg escape hatch `canSuggestSupplier` keeps for
// exactly this: a host that has not (yet, or ever) told this sheet its
// carton's status gets the three-arm, line-only answer, same as before.
const box = computed(() =>
  props.boxStatus === undefined
    ? undefined
    : { status: props.boxStatus, deliveredConfirmedAt: props.boxDeliveredConfirmedAt },
);
const canSuggest = computed(() =>
  canSuggestSupplier(props.item, box.value) && !stillWaiting.value,
);

// «غيّر اقتراحك» — correcting an OPEN tip is still hers, but only while the
// line ITSELF still qualifies (tips-wave P3-A, U-80/W-C). `stillWaiting` alone
// used to gate this link, which offered "change it" on a line whose carton had
// since moved on (U-69's carton gate) or that had already spent its one tip —
// a tap that could only 403/422. `canSuggest` cannot be reused here: it is
// `canSuggestSupplier && !stillWaiting`, false BY DEFINITION exactly while a
// tip is open.
const canCorrectSuggestion = computed(
  () => stillWaiting.value && canSuggestSupplier(props.item, box.value),
);

// One writer for all four surfaces that render this receipt, so the row, this
// sheet, a resolved question's card and the post card cannot drift apart.
const suggestionReceipt = computed(() =>
  suggestionLabel(suggestion.value, { t, locale: locale.value, formatMoney: formatCurrency }),
);

// ── «أرجّع الصنف» — her post-delivery return (returns-desk P3-A part A) ──────
//
// `box` above already carries the one fact this reads (status +
// deliveredConfirmedAt) for the suggest-a-shop door; the return door shares
// the same carton-eligibility shape, so it reads the same computed rather
// than re-deriving it.
const supplierReturn = computed(() => readSupplierReturn(props.item));
// «تدبيسة النادي» resolves once per signed-in user (useTadbeesaChannel) — a
// line from the club's own shelf has no return door, final sale (2026-09-22
// brief). While the lookup is unresolved this reads null, which is the safe
// direction: `canReturn` below only ever stays OFFERED a beat too long, never
// wrongly hidden on an ordinary line.
const { channelId: tadbeesaChannelId } = useTadbeesaChannel();
const isTadbeesaItem = computed(() => isTadbeesaLine(props.item, tadbeesaChannelId.value));
const canReturn = computed(() => canRequestReturn(props.item, box.value, tadbeesaChannelId.value));
// Where the return door WOULD have shown but for being a تدبيسة line — every
// other eligibility fact holds, only the تدبيسة exclusion refuses it. The
// final-sale note replaces the door so she isn't left looking for a control
// that will never appear on a piece from the club's own shelf.
const showTadbeesaFinalSale = computed(() =>
  isTadbeesaItem.value && canRequestReturn(props.item, box.value, null),
);
const returnStatusText = computed(() =>
  returnStatusLabel(supplierReturn.value, { t }),
);
const returnReasonText = computed(() =>
  returnReasonLabel(supplierReturn.value, { t }),
);

function openRequestReturnSheet() {
  // Same "step aside" reasoning as openSuggestSheet: two stacked sheets is the
  // nesting this codebase keeps flattening out.
  sheetsState.details = false;
  sheetsState.requestReturn = true;
}

/**
 * The response already carries the fresh `supplierReturn` (the repository
 * saved it to the ORM before this fires), so `props.item` is live and the
 * status block below paints with no extra read.
 */
function onReturnSubmitted() {
  f7.toast
    .create({
      text: t('item.return.success'),
      closeTimeout: 2500,
      horizontalPosition: 'center',
    })
    .open();
}

/**
 * The door was offered on a line the policy has since refused — re-read the
 * box so a stale door does not sit there offering itself again, the same
 * backstop `onSuggestionStale` runs.
 */
function onReturnStale({ message }) {
  showError(message);

  const boxId = props.item?.orderBoxId;
  if (boxId == null) return;
  OrderBox.fetchOne(boxId).catch((err) => {
    console.warn('onReturnStale: box refresh failed (non-fatal)', err);
  });
}

// ── CANCEL AN OUT-OF-STOCK LINE ─────────────────────────────────────────────
//
// The line kept her money the whole time nobody could find the product — it is
// held, not spent, and nothing was ever bought. Cancelling is how she gets it
// back, and it is the only member action that does.
const cancelling = ref(false);

// `canCancel` already carries all three gates (status, `inActivePlan`, the
// carton). This narrows it to the ONE status this control is for, so a PENDING
// line keeps using the edit sheet's cancel and does not grow a second one here.
const canCancelOutOfStock = computed(
  () => itemStatus.currentStatus.value === ItemStatus.OUT_OF_STOCK && itemStatus.canCancel.value,
);

/**
 * Confirmed, because it is genuinely irreversible from where she stands: the
 * club stops looking, and re-adding the line is a new order at whatever the
 * price is then. That is the narrow bar this repo's UX rule keeps a confirm
 * for — not every destructive-looking tap, but the ones an undo toast cannot
 * honestly offer.
 *
 * `dialog.create` with explicit buttons rather than `dialog.confirm`: the
 * shorthand renders English OK/Cancel regardless of locale (the defect
 * WalletPage documents at its own confirm).
 *
 * The buttons are «رجوع» and «إلغاء الصنف», not «إلغاء» and «تأكيد» (carton
 * «نواقص» tab, owner decision 2026-09-21). With the generic pair, the button
 * that KEEPS the item said «إلغاء», right beside the question about cancelling
 * it. The ship-ready confirm made the same call.
 *
 * Also reached from the row itself: the «نواقص» tab's «إلغاء الصنف» button
 * calls this through `defineExpose`, so there is one confirm for both doors.
 */
/** Same escape OrdersPage uses for its ask-to-ship confirm. */
const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));

function confirmCancelOutOfStock() {
  if (cancelling.value) return;

  // The item's own name heads the text. From the «نواقص» tab this confirm is
  // opened by a row button, not from inside the item's sheet, so without it a
  // mis-tap on the neighbouring row would ask about "this item" and cancel the
  // wrong one. Escaped: `text` is HTML and a manual line's name is typed by hand.
  const name = itemDisplayName(props.item);
  const message = escapeHtml(t('item.status.confirmations.cancelOutOfStockMessage'));

  f7.dialog
    .create({
      title: t('item.status.confirmations.cancelOutOfStockTitle'),
      text: name
        ? `<p style="margin: 0 0 8px; font-weight: 600;">${escapeHtml(name)}</p>${message}`
        : message,
      buttons: [
        { text: t('general.back'), close: true },
        {
          text: t('item.status.actions.cancelOutOfStock'),
          color: 'red',
          close: true,
          onClick: runCancelOutOfStock,
        },
      ],
      verticalButtons: false,
    })
    .open();
}

async function runCancelOutOfStock() {
  const itemId = props.item?.id;
  if (itemId == null || cancelling.value) return;
  // Captured BEFORE the await: from the «نواقص» tab she can tap another row
  // while this request is in flight, and `props.item` then names that one.
  const boxId = props.item?.orderBoxId;

  cancelling.value = true;
  try {
    const updated = await cancelOrderBoxItem(itemId, null);
    useRepo(OrderBoxItem).save(updated);
    // Only close the sheet if it still shows the line that was cancelled.
    if (props.item?.id === itemId) sheetsState.details = false;
    showSuccess(t('item.status.feedback.outOfStockCancelled'));

    // The carton's totals and her committed balance both moved, so the box is
    // re-read rather than patched: the released hold is a DERIVED number on the
    // wallet, never a transaction, and nothing local can compute it. The wallet
    // tab (if already mounted — F7 keeps routable tabs alive once loaded) hears
    // the same signal and re-reads its own balance.
    if (boxId != null) {
      OrderBox.fetchOne(boxId).catch((err) => {
        console.warn('cancelOutOfStock: box refresh failed (non-fatal)', err);
      });
    }
    walletRefreshBus.emit();
  } catch (error) {
    // The 422's detail is HER OWN ARABIC SENTENCE now (the backend stopped
    // sending the English service exception), so it is preferred over our copy
    // exactly as every other refusal on this surface is. It means the carton
    // moved on while she was looking — so the line is re-read, which is what
    // makes the control disappear rather than merely refuse again.
    showError(error?.detail || t('item.status.errors.cannotCancel'));
    if (boxId != null) OrderBox.fetchOne(boxId).catch(() => {});
  } finally {
    cancelling.value = false;
  }
}

// ── «اطلب الإلغاء» — HER OTHER DOOR ─────────────────────────────────────────
//
// The complement of the cancel above (BE→FE brief
// 2026-09-21-member-cancel-request): a collector is already out buying this
// line, so `canCancel` withholds the plain cancel, and this is what she does
// instead. Nothing is bought and nothing moves yet — the admin decides — so
// unlike `cancelling` above there is no wallet refresh to fire and no success
// toast to show: the button flipping to the waiting chip in place is the only
// confirmation this needs.
const requestingCancel = ref(false);

// `canRequestCancel` already carries the three gates (status, `inActivePlan`,
// no request already waiting) — read here rather than re-derived, so the
// button and the model's own `canRequestCancel` getter can never disagree.
const canRequestCancelHere = computed(() => canRequestCancel(props.item));
// Presence-driven, like every other omitted-key fact this sheet reads.
const hasPendingCancelRequestHere = computed(() => hasPendingCancelRequest(props.item));

/**
 * ONE confirm sentence (owner decision, 2026-09-21): the admin decides, and
 * until they answer the item may still be bought. Kept as a confirm because
 * the request pings the admin at once and cannot be taken back. Same
 * escaped-name heading, same back-then-red-action buttons and
 * `verticalButtons: false` as `confirmCancelOutOfStock`.
 */
function confirmRequestCancel() {
  if (requestingCancel.value) return;

  const name = itemDisplayName(props.item);
  const message = escapeHtml(t('item.status.confirmations.requestCancelMessage'));

  f7.dialog
    .create({
      title: t('item.status.confirmations.requestCancelTitle'),
      text: name
        ? `<p style="margin: 0 0 8px; font-weight: 600;">${escapeHtml(name)}</p>${message}`
        : message,
      buttons: [
        { text: t('general.back'), close: true },
        {
          text: t('item.status.actions.requestCancel'),
          color: 'red',
          close: true,
          onClick: runRequestCancel,
        },
      ],
      verticalButtons: false,
    })
    .open();
}

async function runRequestCancel() {
  const itemId = props.item?.id;
  if (itemId == null || requestingCancel.value) return;
  // Captured before the await, same reasoning as `runCancelOutOfStock`.
  const boxId = props.item?.orderBoxId;

  requestingCancel.value = true;
  try {
    const updated = await requestCancelOrderBoxItem(itemId);
    useRepo(OrderBoxItem).save(updated);
    // The sheet stays OPEN and there is no toast — `props.item` now carries
    // `cancelRequestedAt`, so the button re-renders as the waiting chip in the
    // same spot, and that IS the confirmation.
  } catch (error) {
    // 422 carries HER OWN ARABIC SENTENCE (the line was bought meanwhile) —
    // shown verbatim, same as the out-of-stock cancel above. The 403 this
    // door can also get (`ORDER_BOX_ITEM_REQUEST_CANCEL_FORBIDDEN`) is an
    // English Laravel sentence and must NOT reach her, so it falls through to
    // the fallback copy instead of `error.detail`.
    if (error?.code === ORDER_BOX_ITEM_STALE_STATE && error?.detail) {
      showError(error.detail);
    } else {
      showError(t('item.status.errors.requestCancelFailed'));
    }
    // Re-read the box either way, so a stale door (offered or already-waiting)
    // disappears rather than sitting there refusing again.
    if (boxId != null) OrderBox.fetchOne(boxId).catch(() => {});
  } finally {
    requestingCancel.value = false;
  }
}

function openSuggestSheet() {
  // Stacked sheets are the nesting this codebase keeps flattening out (the
  // add-to-box audit), so the details sheet steps aside rather than sitting
  // behind this one.
  sheetsState.details = false;
  sheetsState.suggest = true;
  // Fire-and-forget: the sheet renders its typed field immediately and the
  // picker appears if and when the roster lands. A failure is "no list", never
  // an error — see useSupplierList. The line's product is what the picker tags
  // «already named» against; an off-catalog line has none, so it gets no tags.
  loadSuppliers(props.item?.productId);
}

/**
 * The POST already returned the updated line and the repository already saved it
 * to the ORM, so the parent's `item` prop is live by the time this fires — the
 * pending state paints with no extra read. Nothing to do but confirm.
 */
function onSuggestionSubmitted() {
  f7.toast
    .create({
      text: t('item.suggestSupplier.success'),
      closeTimeout: 2500,
      horizontalPosition: 'center',
    })
    .open();
}

/**
 * The line moved past suggesting while she was typing (403). The sheet has
 * already closed itself, so this says what happened and RE-READS the box — the
 * refusal is the first news we have that the line's status is stale, and without
 * the re-read the door she just failed at is still sitting there offering itself.
 *
 * Fire-and-forget for the same reason `cancelItem` refreshes that way: the toast
 * is the member's answer, and a slow box read must not hold it back.
 *
 * Through the MODEL's own `fetchOne`, not OrderBoxRepository's: this component is
 * already holding the model, and the repository pulls `useAuth` behind it, which
 * reaches `usePwa` and its `virtual:pwa-register/vue` import — unresolvable in
 * the unit-test environment. Same request, same `save: true`, no new dependency.
 */
function onSuggestionStale({ message }) {
  // `showError`, not a hand-rolled f7.toast: IssueDecisionPage speaks this exact
  // sentence through useToast, and one refusal reading as an error on one
  // surface and a neutral notice on the other is a difference the member has no
  // way to interpret. useToast also carries the ARIA live-region attributes F7
  // omits.
  showError(message);

  const boxId = props.item?.orderBoxId;
  if (boxId == null) return;
  OrderBox.fetchOne(boxId).catch((err) => {
    console.warn('onSuggestionStale: box refresh failed (non-fatal)', err);
  });
}

function openIssueHistory() {
  const id = readableIssueId.value;
  if (id == null) return;
  // Close first: navigating out from under an open sheet leaves the backdrop
  // stranded over the page we just pushed.
  sheetsState.details = false;
  openIssue(id);
}

const canEdit = computed(() => {
  if (![OrderBoxStatus.DRAFT, OrderBoxStatus.ACTIVE].includes(props.boxStatus)) return false;
  return itemStatus.isEditable.value;
});

const unitPrice  = computed(() => (props.item ? formatCurrency(props.item.price) : ''));

// NET line total, not gross. `price * qty` is the retail figure; a Poola line
// carries a PoolShare-funded `discount`, so the cash the member owes is the
// backend-generated `amount` (see OrderBoxItemResource + OrderBoxItem.totalPrice).
// Reuses knownLineNetCents (the amount-first precedence SSOT) rather than
// re-deriving it here.
const totalCents = computed(() => knownLineNetCents(props.item));
// knownLineNetCents returns null — never 0 — for a line whose net genuinely
// cannot be derived. Follow this surface's convention and OMIT the row: an
// invented "0.00 ج.م" in the sheet a member opens to check what they owe reads
// as a real figure, and a missing row does not.
const totalPrice = computed(() =>
  totalCents.value == null ? '' : formatCurrency(totalCents.value),
);

// Manual (off-catalog) line handling — see utils/orderBoxItemDisplay.js.
const isManual    = computed(() => isManualItem(props.item));
const displayName = computed(() => itemDisplayName(props.item));

const timeline = computed(() => {
  const { item } = props;
  if (!item) return [];

  const timelineItems = [];

  // ── "Ordered" entry — always the first event ────────────────────────────────
  if (item.orderedAt) {
    const formatted = formatDateTime(item.orderedAt, locale.value);
    if (formatted) {
      const meta = ItemStatusMeta[ItemStatus.PENDING];
      timelineItems.push({
        date:        formatted.date,
        time:        formatted.time,
        title:       t(`${meta.i18nKey}.label`),
        subtitle:    t('item.timeline.ordered'),
        description: '',
      });
    }
  }

  // ── Collection workflow entries (date-stamped statuses) ─────────────────────
  const statusDateMap = {
    [ItemStatus.COLLECTING]: item.collectingStartedAt,
    [ItemStatus.COLLECTED]:  item.collectedAt,
    [ItemStatus.PACKAGED]:   item.packagedAt,
    [ItemStatus.SHIPPED]:    item.shippedAt,
    [ItemStatus.DELIVERED]:  item.deliveredAt,
  };

  for (const [status, date] of Object.entries(statusDateMap)) {
    if (!date) continue;
    const formatted = formatDateTime(date, locale.value);
    if (!formatted) continue;
    const meta = ItemStatusMeta[status];
    timelineItems.push({
      date:        formatted.date,
      time:        formatted.time,
      title:       t(`${meta.i18nKey}.label`),
      subtitle:    '',
      description: t(`${meta.i18nKey}.description`),
    });
  }

  // ── Cancelled entry ─────────────────────────────────────────────────────────
  if (item.status === ItemStatus.CANCELLED && item.cancelledAt) {
    const formatted = formatDateTime(item.cancelledAt, locale.value);
    if (formatted) {
      const meta = ItemStatusMeta[ItemStatus.CANCELLED];
      timelineItems.push({
        date:        formatted.date,
        time:        formatted.time,
        title:       t(`${meta.i18nKey}.label`),
        subtitle:    t('item.timeline.cancelled'),
        description: t('item.timeline.itemCancelled'),
      });
    }
  }

  // ── Issue entry ─────────────────────────────────────────────────────────────
  if (itemStatus.isIssue.value && item.issueDetectedAt) {
    const formatted = formatDateTime(item.issueDetectedAt, locale.value);
    if (formatted) {
      timelineItems.push({
        date:        formatted.date,
        time:        formatted.time,
        title:       localizedStatus.value,
        subtitle:    t('item.timeline.issue'),
        description: t('item.timeline.issueDescription'),
      });
    }
  }

  // ── Return entry ─────────────────────────────────────────────────────────────
  // Pushed LAST — after `.reverse()` below it lands FIRST (most recent), which
  // is always true here: a return on a line can only open once the line itself
  // is DELIVERED. No timestamp travels with `supplierReturn` (it carries only
  // `status` and `reasonKey` — see utils/supplierReturn.js), so this carries no
  // date, unlike every entry above it.
  if (supplierReturn.value) {
    timelineItems.push({
      date:        '',
      time:        '',
      title:       returnStatusText.value,
      subtitle:    t('item.timeline.return'),
      description: '',
    });
  }

  return timelineItems.reverse();
});

// Methods
const openDetails = () => {
  sheetsState.details = true;
  // NO EXTRA READ ANY MORE (oos-hold P3-A). This used to fire
  // `OrderBoxRepository.refreshSupplierSuggestion()` because an ANSWERED tip
  // travelled only on a request that named the attribute in a sparse fieldset —
  // the backend's U-40 gate, which existed to protect a shipped app that would
  // otherwise have rendered «اقتراحك وصل للأدمن» over an answered tip for thirty
  // days. That gate was DELETED on 2026-09-15 (brief
  // `2026-09-15-roster-only-tips-once-per-line.md`, change 5): the answer now
  // rides `supplierSuggestion` on every ordinary read of the line, which is
  // where `props.item` already gets it from. So opening this sheet costs one
  // request fewer and shows exactly the same receipt.
};

const fetchPostAllData = async () => {
  // Prevent concurrent requests
  if (loading.value || !props.item) return;

  try {
    loading.value = true;
    error.value = null;

    await fetchPostData(0, props.item.postId);

    await nextTick(); // Allow save to complete before proceeding
    post.value = useRepo(Post).with('channel').with('products').with('media').find(props.item.postId);
    media.value = post.value.media;
  } catch (err) {
    if (err.name === 'AbortError' || err.name === 'CanceledError') {
      return;
    }
    error.value = err.message || 'Failed to load post data';
    console.error('[ItemSheets] Fetch error:', err);
  } finally {
    loading.value = false;
  }
};

const openEditSheet = async () => {
  if (!props.item) return;
  const { item } = props;

  // Close details sheet
  sheetsState.details = false;

  // B6: post data (variants/media) loads lazily here instead of on list mount.
  // The sheet opens immediately and shows a preloader until data resolves.
  sheetsState.edit = true;
  if (!loadedPostIds.has(item.postId)) {
    await fetchPostAllData();
    if (error.value) {
      sheetsState.edit = false;
      f7.toast.create({
        text: t('errors.loadFailed'),
        position: 'center',
        closeTimeout: 3000,
      }).open();
      return;
    }
    loadedPostIds.add(item.postId);
  } else {
    // Cached post — still refresh the media ref for this item's post.
    post.value = useRepo(Post).with('channel').with('products').with('media').find(item.postId);
    media.value = post.value?.media ?? null;
  }

  // Load variant data
  const variant = useRepo(Variant).find(item.variantId);
  if (!variant) {
    sheetsState.edit = false;
    f7.dialog.alert(t('item.feedback.variantNotFound'), t('general.error'));
    return;
  }

  // Load order box for ship name
  const box = useRepo(OrderBox).find(item.orderBoxId);
  boxShipName.value = box?.shipName || '';

  // Load all items for this variant
  const items = loadVariantItems(
    item.orderBoxId,
    item.productId,
    item.variantId,
  );

  // Set variant data and items
  variantData.value = {
    id: variant.id,
    name: variant.name,
    price: variant.price,
  };
  currentVariantItems.value = items;
};

const handleEditSheetClose = () => {
  // Clean up state when sheet closes
  variantData.value = null;
  currentVariantItems.value = [];
  sheetsState.edit = false;
};

const handleCancelVariant = () => {
  sheetsState.edit = false;
};

const handleVariantSaveSuccess = () => {
  sheetsState.edit = false;

  // Refresh is handled by VariantItemsEditor's save operation
  // which updates the Pinia ORM store automatically

  f7.toast.create({
    text: t('item.feedback.itemsUpdated'),
    position: 'center',
    closeTimeout: 2000,
  }).open();
};

const handleVariantSaveError = (saveError) => {
  console.error('Variant save failed:', saveError);
  f7.dialog.alert(t('item.feedback.updateFailed'), t('general.error'));
};

const closeAllSheets = () => {
  sheetsState.details = false;
  sheetsState.edit = false;
  sheetsState.requestReturn = false;
};

watch(() => props.isUpdating, (isUpdating) => {
  if (!isUpdating) {
    closeAllSheets();
  }
});

// `openSuggest` and `confirmCancelOutOfStock` are the «نواقص» tab's two row
// buttons (carton-oos P1-B). The row sets the item first, then calls straight
// into the same door and the same one-layer confirm the details sheet uses, so
// the row and the sheet cannot drift apart.
defineExpose({
  openDetails,
  openSuggest: openSuggestSheet,
  confirmCancelOutOfStock,
  closeAllSheets,
});
</script>

<style scoped>
.edit-sheet-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--poolit-spacing-xxl) var(--poolit-spacing-md);
}

.item-overview {
    padding: 16px;
}

.overview-header {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}

.overview-image {
    width: 80px;
    height: 80px;
    border-radius: 8px;
    object-fit: cover;
}

.overview-info {
    flex: 1;
}

/* Manual-line hint — soft brand-tinted panel under the header. */
.manual-hint {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 14px 16px 2px;
    padding: 10px 12px;
    border-radius: var(--poolit-radius-md, 8px);
    background: var(--poolit-primary-50, #f5f0ff);
    color: var(--poolit-primary-800, #5204d4);
    font-size: 13px;
    line-height: 1.5;
}

.manual-hint__icon {
    flex-shrink: 0;
    margin-top: 1px;
}

.dark .manual-hint {
    background: rgba(142, 91, 254, 0.14);
    color: var(--poolit-primary-200, #d7c2ff);
}

.overview-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 4px;
}

.overview-subtitle {
    font-size: 14px;
    color: var(--f7-text-color);
    margin-bottom: 8px;
}

.card-divider {
    height: 1px;
    background-color: var(--f7-list-item-border-color);
}

.item-details-list {
    padding: 16px;
}

.detail-label {
    font-weight: 500;
}

.detail-total {
    font-weight: 600;
    color: var(--f7-theme-color);
}

.item-notes {
    padding: 0 16px 16px 16px;
}

.notes-title {
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--f7-text-color);
}

.notes-content {
    padding: 12px;
    background-color: var(--f7-block-strong-bg-color);
    border-radius: 8px;
    font-size: 14px;
}

/* Calm issue-aware state block (being-handled / out-of-stock) — an honest,
   non-actionable status, never the retired red resolution CTA. */
.issue-state {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    border-radius: var(--poolit-radius-md, 12px);
    background: var(--ci-brand-bg, #f0eaff);
}

.issue-state--out-of-stock {
    background: var(--ci-muted-bg, #eceef1);
}

.issue-state__icon {
    flex-shrink: 0;
    color: var(--ci-brand-fg, #6621e2);
}

.issue-state--out-of-stock .issue-state__icon {
    color: var(--ci-muted-fg, #374151);
}

.issue-state__title {
    font-size: 15px;
    font-weight: var(--poolit-font-weight-semibold, 600);
    color: var(--poolit-text-primary);
    margin-bottom: 4px;
}

.issue-state__body {
    font-size: 13px;
    line-height: 1.5;
    color: var(--poolit-text-secondary);
}

/* The money sentence, set apart from the body above it: it is a different KIND
   of fact — what is happening to her money, not what is happening to the line —
   and it is the one the cancel button below acts on. */
.issue-state__money {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--poolit-border, #e2e5e3);
    font-size: 12px;
    line-height: 1.5;
    color: var(--poolit-text-muted);
}

/* Destructive, so it never wears a fill: the only filled button in this sheet
   is Edit, which is the ordinary thing to do. */
.cancel-oos__btn {
    width: 100%;
}

/* Her other door — «اطلب الإلغاء». Same full-width weight as cancel-oos above,
   and the same reason: this is hers to do. */
.request-cancel__btn {
    width: 100%;
}

/* The waiting chip that takes the button's place once she has used the door.
   Quiet, amber — a fact about what she asked, never a verdict. */
.request-cancel--pending {
    display: flex;
}

/* "See what happened" — a look-up, so it stays quieter than the edit CTA below
   it: the feature's calm brand outline rather than a fill. */
.issue-history__btn {
    --f7-button-border-color: var(--ci-border, #c8d2cd);
    --f7-button-text-color: var(--ci-brand-fg, #6621e2);
}

/* Her door, and the state it becomes once she has used it. Deliberately the
   same weight as .issue-history below/above it: both are look-ups and offers,
   neither is something the club is asking of her. */
.suggest-supplier {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.suggest-supplier__btn {
    width: 100%;
}

.suggest-supplier__pending {
    margin: 0;
    font-size: var(--poolit-font-size-sm);
    line-height: 1.5;
    color: var(--poolit-text-secondary);
}

/* Her return door — same weight as .suggest-supplier above. */
.request-return__btn {
    width: 100%;
}

/* Her return's status — quiet, a fact rather than a verdict, same register as
   the suggestion receipt beside it. */
.return-status {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.return-status__text {
    margin: 0;
    font-size: var(--poolit-font-size-sm);
    line-height: 1.5;
    color: var(--poolit-text-secondary);
}

/* «قطع التدبيسة مالهاش إرجاع» — quiet, same weight as .return-status: a fact,
   not a verdict. Measured contrast (2026-09-23 design pass): #4b5563 on white
   is 7.56:1. */
.tadbeesa-final-sale {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--poolit-font-size-sm);
    line-height: 1.5;
    color: #4b5563;
}

.tadbeesa-final-sale__icon {
    color: #4b5563;
    flex-shrink: 0;
}

</style>
